import { PrismButton, PrismPopover, PrismRadio, PrismRadioGroup } from "@prism-ui/react";
import type { SortMode } from "./projects.utils";
import "./Projects.css";

interface SortControlProps {
  value: SortMode;
  onChange: (mode: SortMode) => void;
}

const SORT_OPTIONS: Array<{ value: SortMode; display: string }> = [
  { value: "random", display: "Random" },
  { value: "newest", display: "Newest First" },
  { value: "oldest", display: "Oldest First" },
  { value: "alpha", display: "A → Z" },
  { value: "reverse-alpha", display: "Z → A" },
];

/**
 * Single-select counterpart to MultiSelectDropdown: the same compact pill trigger
 * and popover, but the body is a PrismRadioGroup so exactly one sort mode is active.
 * The group owns mutual exclusivity — selecting one deselects the rest — and emits
 * the chosen value via prismChange. "random" is the default, so any other choice
 * marks the pill as active.
 */
export function SortControl({ value, onChange }: SortControlProps) {
  const isActive = value !== "random";

  return (
    <PrismPopover
      as="context"
      placement="bottom-start"
      arrow={false}
      className="projects-filter-dropdown"
    >
      <PrismButton
        slot="reference"
        className={`projects-filter-trigger${isActive ? " is-active" : ""}`}
        tone="secondary"
        display="outline"
        size="sm"
        aria-label="Sort projects"
      >
        <span className="projects-filter-trigger-label">Sort</span>
        <span className="projects-filter-caret" aria-hidden="true" />
      </PrismButton>

      <div className="projects-filter-menu">
        <PrismRadioGroup
          className="projects-sort-radiogroup"
          legend="Sort projects"
          showLegend={false}
          layout="stacked"
          value={value}
          onPrismChange={(e: CustomEvent<{ value?: string }>) => {
            const detail = e.detail as { value?: string } | string | undefined;
            const next = (typeof detail === "string" ? detail : detail?.value) as
              | SortMode
              | undefined;
            if (next) onChange(next);
          }}
        >
          {SORT_OPTIONS.map((o) => (
            <PrismRadio
              key={o.value}
              className="projects-filter-check"
              name="projects-sort"
              label={o.display}
              value={o.value}
            />
          ))}
        </PrismRadioGroup>
      </div>
    </PrismPopover>
  );
}
